import PropTypes from "prop-types"
import { Suspense, useState } from "react"
import { useSelector } from "react-redux"

import useCollection from "areas/collection/hoc/use-collection"
import withWishlist from "areas/collection/hoc/with-wishlist"
import Confirmation from "components/ui/confirmation"
import { IS_JEWLR } from "helpers/application"
import { cn } from "styles/utils"

import CollectionAd from "./collection-ad"
import CollectionProduct from "./collection-product"
import Placeholder from "./placeholder"

export const GridContainer = ({ className, ...props }) => {
  return (
    <ul
      className={cn(
        "tablet:grid-cols-3 widescreen:grid-cols-4 desktop:gap-y-40 tablet:gap-x-24 grid list-none grid-flow-dense grid-cols-2 gap-x-16 gap-y-24",
        className
      )}
      {...props}
    />
  )
}

GridContainer.propTypes = {
  className: PropTypes.string,
}

const InternalCollectionMain = ({
  addToWishlist,
  confirmAddToWishlist,
  newPageFocusRef,
}) => {
  const [showWishlistDialog, setShowWishlistDialog] = useState(false)
  const [productParams, setProductParams] = useState(false)
  const loading = useSelector((state) => state.collection.loading)
  const {
    data: {
      pagination: {
        next_page_product_count: nextPageCount,
        results_per_page: resultsPerPage = 24,
      } = {},
      placeholders = 0,
      product_result: products = [],
    } = {},
    isFetchingNextPage,
  } = useCollection()
  const wishlistName = IS_JEWLR() ? "wish list" : "collection"
  const listName = IS_JEWLR() ? "list" : "collection"

  if (!loading && products?.length === 0) {
    return (
      <div
        className="flex flex-wrap items-center justify-center"
        data-cy="collection-not-result"
      >
        <p className="text-20 my-32 text-center">
          Sorry, we could not find any results for your search. Please try a
          different search term.
        </p>
      </div>
    )
  }

  return (
    <>
      <GridContainer data-cy="collection-main-products">
        {loading ? (
          resultsPerPage > 0 &&
          [...Array(resultsPerPage)].map((_, i) => (
            <Placeholder
              dataCy={`collection-loading-placeholder-${i}`}
              index={i}
              key={`loading-placeholder-${i}`}
            />
          ))
        ) : (
          <>
            {placeholders > 0 &&
              [...Array(placeholders)].map((_, i) => (
                <Placeholder
                  dataCy={`collection-loading-placeholder-${i}`}
                  index={i}
                  key={`collection-loading-placeholder-${i}`}
                />
              ))}
            {products.map((product, i) =>
              product.item_type === "ad_block" ? (
                <CollectionAd
                  ad={product}
                  index={i}
                  key={`ad-product-${product.style_code}-${i}`}
                  newPageFocusRef={newPageFocusRef}
                />
              ) : (
                <CollectionProduct
                  confirmWishlist={setShowWishlistDialog}
                  index={i}
                  key={`product-${product.style_code}-${i}`}
                  newPageFocusRef={newPageFocusRef}
                  product={product}
                  setProductParams={setProductParams}
                />
              )
            )}
            {isFetchingNextPage &&
              [...Array(nextPageCount)].map((_, i) => (
                <Placeholder
                  dataCy={`collection-next-page-placeholder-${i}`}
                  index={i}
                  key={`loading-more-products-${i}`}
                />
              ))}
          </>
        )}
      </GridContainer>
      {typeof window !== "undefined" && (
        <Suspense fallback={<div />}>
          <Confirmation
            cancelText="Cancel"
            confirmText="Proceed"
            description={`You have reached the maximum number of items on your ${wishlistName}. Adding this item will remove the oldest from your ${listName}.`}
            onCancel={() => {
              confirmAddToWishlist(false)
              setShowWishlistDialog(false)
            }}
            onConfirm={() => {
              confirmAddToWishlist(true)
              setShowWishlistDialog(false)
              addToWishlist(productParams)
            }}
            show={showWishlistDialog}
            title={`Add to ${wishlistName.titleCase()}`}
          />
        </Suspense>
      )}
    </>
  )
}

InternalCollectionMain.propTypes = {
  addToWishlist: PropTypes.func,
  confirmAddToWishlist: PropTypes.func,
  newPageFocusRef: PropTypes.object,
}

const CollectionMain = withWishlist(InternalCollectionMain)

export default CollectionMain
